import {
    formatElapsedMilliseconds,
    formatCompactDateTime,
} from '../utils/formatters';
import { IBrokerConnectionActiveConversationsData } from '../model/brokerConnections/IBrokerConnectionActiveConversationsData';
import { ActiveConversationRow } from '../model/brokerConnections/ActiveConversationRow';

export const safeLocaleCompare = (a?: string, b?: string) =>
    (a || '').localeCompare(b || '');

const parseUtcDate = (dateString?: string | null): number => {
    if (!dateString) return 0;

    const normalizedDate = /[zZ]|[+-]\d{2}:?\d{2}$/.test(dateString)
        ? dateString
        : `${dateString}Z`;

    const parsedDate = Date.parse(normalizedDate);
    return isNaN(parsedDate) ? 0 : parsedDate;
};

export const safeDateCompare = (aRaw?: string, bRaw?: string) => {
    const av = parseUtcDate(aRaw);
    const bv = parseUtcDate(bRaw);
    if (isNaN(av) && isNaN(bv)) return 0;
    if (isNaN(av)) return -1;
    if (isNaN(bv)) return 1;
    return av - bv;
};

export const safeFlightCompare = (a?: string, b?: string) => {
    const av = (a || '').trim();
    const bv = (b || '').trim();
    if (av === bv) return 0;
    const allDigitsA = /^\d+$/.test(av);
    const allDigitsB = /^\d+$/.test(bv);
    if (allDigitsA && allDigitsB) {
        return Number(av) - Number(bv);
    }
    const numA = av.match(/\d+/)?.[0];
    const numB = bv.match(/\d+/)?.[0];
    if (numA && numB) {
        const nA = Number(numA);
        const nB = Number(numB);
        if (nA !== nB) return nA - nB;
        return av.localeCompare(bv);
    }
    return av.localeCompare(bv);
};

export const calculateHandlingTime = (
    data: IBrokerConnectionActiveConversationsData,
    now: number
): string => {
    const handlingTimeMs = (data.totalHandlingTimeSeconds ?? 0) * 1000;
    const isGroundInitiated = data.initiatedFrom === 'Ground';

    // Timer is paused - only show accumulated time
    if (data.isPauseStatus) {
        return handlingTimeMs > 0
            ? formatElapsedMilliseconds(handlingTimeMs)
            : '';
    }

    // Timer is running - calculate current segment to add to accumulated time
    let segmentStartMs = 0;

    // We have accumulated time, so conversationStatusUpdatedOnUtc should be when we last resumed
    if (handlingTimeMs > 0 && data.conversationStatusUpdatedOnUtc) {
        segmentStartMs = Date.parse(data.conversationStatusUpdatedOnUtc);
    }
    // Ground-initiated conversation with no accumulated time yet - timer started when status changed away from "Awaiting Crew Response"
    else if (isGroundInitiated && data.conversationStatusUpdatedOnUtc) {
        segmentStartMs = Date.parse(data.conversationStatusUpdatedOnUtc);
    }
    // Flight crew-initiated or no status change yet - use conversation creation time
    else {
        const createdRaw = data.createdOnUtc;
        segmentStartMs = createdRaw ? Date.parse(createdRaw) : 0;
    }

    // Add current time segment to accumulated time
    if (segmentStartMs > 0) {
        const currentSegmentMs = now - segmentStartMs;
        if (currentSegmentMs > 0) {
            return formatElapsedMilliseconds(handlingTimeMs + currentSegmentMs);
        }
    }

    return handlingTimeMs > 0 ? formatElapsedMilliseconds(handlingTimeMs) : '';
};

export const checkIsArrivingSoon = (arrivalTime: string): boolean => {
    const t = Date.parse(arrivalTime);
    if (isNaN(t)) return false;

    const diffMs = t - Date.now();
    const sixtyMinutesMs = 60 * 60 * 1000;
    return diffMs <= sixtyMinutesMs;
};

export const convertDataToRow = (
    data: IBrokerConnectionActiveConversationsData,
    now: number
): ActiveConversationRow => {
    const NEW_THRESHOLD_MS = 5 * 60 * 1000; // Flag window (5 minutes)
    const RECENT_NOTE_THRESHOLD_MS = 10 * 60 * 1000;
    const readAt = data.isRead;
    const createdRaw = data.createdOnUtc;
    const createdMs = createdRaw ? Date.parse(createdRaw) : 0;
    const latestNoteRaw = data.latestConversationNoteTime;
    const latestNoteMs = parseUtcDate(latestNoteRaw);
    const elapsedMs = createdMs > 0 ? now - createdMs : 0;
    const latestMessageMs = parseUtcDate(data.latestBrokerMessageTime);

    const displayHandlingTime = calculateHandlingTime(data, now);

    return {
        conversationId: data.id,
        flight: data.iataFlightIdentifier,
        route: `${data.departureAirportIata} - ${data.arrivalAirportIata}`,
        arrivalTime: data.arrivalTimeUtc,
        dateTime: formatCompactDateTime(createdRaw),
        dateTimeRaw: createdRaw,
        connection: data.connectionStatus,
        read: readAt ? formatCompactDateTime(readAt) : 'No',
        readAtRaw: readAt,
        resolved: data.isResolved ? data.isResolved : 'No',
        conversationStatus: data.isAutoConnection
            ? 'Auto Generated'
            : data.conversationStatus,
        categories: data.categories.join(', '),
        teamsLink: data.teamsLink,
        acReg: data.acReg || '',
        isNew: createdMs > 0 && now - createdMs < NEW_THRESHOLD_MS,
        initiatedFrom: data.initiatedFrom,
        awaitingGroundResponse: data.awaitingGroundTeamResponse,
        elapsedTime: formatElapsedMilliseconds(elapsedMs),
        elapsedMs: elapsedMs,
        handlingTime: displayHandlingTime,
        conversationColor: data.conversationStatusColorFlag,
        conversationStatusLastUpdatedUtc: data.conversationStatusUpdatedOnUtc || '',
        conversationStatusPriority: data.conversationStatusPriority ?? Number.MAX_SAFE_INTEGER,
        isPauseStatus: data.isPauseStatus,
        groundTeamOwner: data.groundTeamOwner,
        groundTeamOwnerId: data.groundTeamOwnerId,
        isArrivingSoon: checkIsArrivingSoon(data.arrivalTimeUtc),
        isAwaitingCrewResponse: data.isAwaitingCrewResponse,
        isAutoConnection: data.isAutoConnection,
        isAutoGeneratedConversation: data.isAutoGeneratedConversation,
        conversationNoteCount: data.conversationNoteCount,
        hasRecentNote: Boolean(
            latestNoteMs > 0 &&
            now - latestNoteMs >= 0 &&
            now - latestNoteMs < RECENT_NOTE_THRESHOLD_MS
        ),
        latestMessageTimeRaw: data.latestBrokerMessageTime ?? createdRaw,
        latestMessageTime:
            latestMessageMs > 0
                ? `${formatElapsedMilliseconds(
                      Math.max(0, now - latestMessageMs)
                  )} ago`
                : ''
    };
};

export const isOpenForHour = (dateTime: string): boolean => {
    return Date.now() - Date.parse(dateTime) > 60 * 60 * 1000;
};
